/**
 * Ask Agent Loop on the portal side: the shared state the /ask page reads,
 * and the call to POST /ask on the server, which streams the answer back
 * as server-sent events (see apps/server/src/routes/ask.ts).
 */

import { createContext, useContext } from 'react';
import { API_BASE } from './apiConfig';
import { supabase } from './supabase';

export type AskTurn = { role: 'user' | 'assistant'; content: string };

export type AskMsg = AskTurn & {
  status?: string | null;
  handoff?: string | null;
  error?: string | null;
  streaming?: boolean;
};

export type AskEvent =
  | { type: 'text'; text: string }
  | { type: 'status'; text: string }
  | { type: 'handoff'; message: string }
  | { type: 'error'; message: string }
  | { type: 'done' };

export type OpenAskOptions = {
  /** The case they opened it from, so "this bill" means that case. */
  caseNumber?: number | null;
  /** Ask this straight away. */
  question?: string;
};

export type AskContextValue = {
  /** Goes to the Ask Agent Loop page. */
  open: (opts?: OpenAskOptions) => void;
  msgs: AskMsg[];
  busy: boolean;
  caseNumber: number | null;
  setCaseNumber: (n: number | null) => void;
  send: (question: string) => void;
  reset: () => void;
  /** Opens Intercom with their question already written. */
  talkToPerson: (prefill?: string | null) => void;
};

export const AskContext = createContext<AskContextValue>({
  open: () => {},
  msgs: [],
  busy: false,
  caseNumber: null,
  setCaseNumber: () => {},
  send: () => {},
  reset: () => {},
  talkToPerson: () => {},
});

export function useAsk(): AskContextValue {
  return useContext(AskContext);
}

export class AskHttpError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/** Sends the conversation and calls onEvent for each piece of the answer. */
export async function streamAsk(
  messages: AskTurn[],
  caseNumber: number | null,
  onEvent: (e: AskEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new AskHttpError(401, 'You need to be signed in.');

  const res = await fetch(`${API_BASE}/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ messages, caseNumber }),
    signal,
  });
  if (!res.ok || !res.body) {
    let msg = '';
    try {
      msg = ((await res.json()) as { error?: string }).error ?? '';
    } catch {
      /* not JSON */
    }
    throw new AskHttpError(res.status, msg || `Request failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let cut: number;
    while ((cut = buf.indexOf('\n\n')) !== -1) {
      const line = buf.slice(0, cut).trim();
      buf = buf.slice(cut + 2);
      if (!line.startsWith('data:')) continue;
      try {
        onEvent(JSON.parse(line.slice(5).trim()) as AskEvent);
      } catch {
        /* ignore a half line */
      }
    }
  }
}
